import { useState, useEffect } from 'react';
import { Menu, X, Clock, AlertTriangle, User } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { supabase, checkSupabaseConnection } from '../lib/supabase';
import { ProModal } from '../components/dashboard/ProModal';
import { ReferralModal } from '../components/dashboard/ReferralModal';
import { ResumeList } from '../components/dashboard/ResumeList';
import { ResumeUploadForm } from '../components/dashboard/ResumeUploadForm';
import { DashboardHeader } from '../components/dashboard/DashboardHeader';
import { UserMenu } from '../components/dashboard/UserMenu';

interface Resume {
  id: string;
  original_filename: string;
  original_file_url: string;
  job_url: string | null;
  job_description: string | null;
  optimized_file_url: string | null;
  status: 'pending' | 'processing' | 'completed' | 'error';
  error_message?: string | null;
  created_at: string;
}

interface UserPlan {
  plan_type: 'free' | 'pro';
  remaining_conversions: number;
}

export function Dashboard() {
  const [isProcessing, setIsProcessing] = useState(false);
  const [resumes, setResumes] = useState<Resume[]>([]);
  const [connectionError, setConnectionError] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showProcessingMessage, setShowProcessingMessage] = useState(false);
  const [userPlan, setUserPlan] = useState<UserPlan | null>(null);
  const [showProModal, setShowProModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [referralCode, setReferralCode] = useState<string | null>(null);
  const [showReferralModal, setShowReferralModal] = useState(false);
  const { user, signOut } = useAuth();

  const remainingResumes = userPlan?.remaining_conversions ?? 0;

  useEffect(() => {
    if (!isLoading && userPlan && remainingResumes <= 0 && userPlan.plan_type === 'free') {
      setShowProModal(true);
    }
  }, [remainingResumes, userPlan, isLoading]);

  useEffect(() => {
    const initializeData = async () => {
      if (user) {
        setIsLoading(true);
        try {
          const isConnected = await checkSupabaseConnection();
          if (!isConnected) {
            setConnectionError(true);
            return;
          }
          setConnectionError(false);
          await Promise.all([fetchResumes(), fetchUserPlan()]);
        } finally {
          setIsLoading(false);
        }
      }
    };

    initializeData();
  }, [user]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isProcessing) {
        e.preventDefault();
        e.returnValue = 'Your CV is still being processed. Closing this window may disrupt the process. Are you sure you want to leave?';
        return e.returnValue;
      }
    };

    if (isProcessing) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isProcessing]);

  useEffect(() => {
    if (user) {
      fetchReferralCode();
    }
  }, [user]);

  const fetchReferralCode = async () => {
    if (!user) return;
    
    try {
      const { data: existingCodes, error: fetchError } = await supabase
        .from('referral_codes')
        .select('code')
        .eq('user_id', user.id)
        .limit(1);

      if (fetchError) throw fetchError;

      if (existingCodes && existingCodes.length > 0) {
        setReferralCode(existingCodes[0].code);
        return;
      }

      const { data: newCode, error: generateError } = await supabase
        .rpc('generate_referral_code', { user_id: user.id });

      if (generateError) throw generateError;
      setReferralCode(newCode);
    } catch (error) {
      console.error('Error fetching/generating referral code:', error);
    }
  };

  const fetchUserPlan = async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('user_plans')
        .select('plan_type, remaining_conversions')
        .eq('user_id', user.id)
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      
      if (!data) {
        const { data: newPlan, error: insertError } = await supabase
          .from('user_plans')
          .insert({ 
            user_id: user.id, 
            plan_type: 'free',
            remaining_conversions: 3 
          })
          .select()
          .single();

        if (insertError) throw insertError;
        setUserPlan(newPlan);
        return;
      }

      setUserPlan(data);
    } catch (error) {
      console.error('Error fetching user plan:', error);
      setUserPlan({ plan_type: 'free', remaining_conversions: 3 });
    }
  };

  const fetchResumes = async () => {
    try {
      const { data, error } = await supabase
        .from('resumes')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setResumes(data || []);
    } catch (error) {
      console.error('Error fetching resumes:', error);
    }
  };

  const handleDownload = async (resume: Resume) => {
    if (!resume.optimized_file_url) {
      alert("Processed file is not available yet");
      return;
    }

    try {
      window.open(resume.optimized_file_url, '_blank');
    } catch (error) {
      console.error("Error downloading file:", error);
      alert("Failed to download file");
    }
  };

  const handleFormSubmit = async (formData: {
    file: File;
    jobUrl: string;
    jobDescription: string;
    isUrlMode: boolean;
    enhanceCV: boolean;
  }) => {
    setIsProcessing(true);
    setShowProcessingMessage(true);

    let resumeId: string | null = null;
    let uploadedFileUrl: string | null = null;

    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) throw new Error('Authentication error: ' + sessionError.message);
      if (!session) throw new Error('No active session');
      if (!user) throw new Error('No user found');

      const { file } = formData;
      if (file.size > 10 * 1024 * 1024) throw new Error('File size must be less than 10MB');
      
      const allowedTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(file.type)) {
        throw new Error('Invalid file type. Please upload a PDF or DOCX file.');
      }

      // Decrement remaining conversions first
      const { data: decremented, error: decrementError } = await supabase
        .rpc('decrement_remaining_conversions', {
          user_id: user.id
        });

      if (decrementError || !decremented) {
        throw new Error('Failed to process conversion: No remaining conversions available');
      }

      try {
        const fileExt = file.name.split('.').pop();
        const fileName = `${Math.random()}.${fileExt}`;
        const filePath = `${user.id}/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('resumes')
          .upload(filePath, file);

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('resumes')
          .getPublicUrl(filePath);

        uploadedFileUrl = publicUrl;
        if (!uploadedFileUrl) throw new Error('Failed to upload file');

        const { data: resumeData, error: resumeError } = await supabase
          .from('resumes')
          .insert({
            user_id: user.id,
            original_filename: file.name,
            original_file_url: uploadedFileUrl,
            job_url: formData.isUrlMode ? formData.jobUrl : null,
            job_description: formData.isUrlMode ? null : formData.jobDescription,
            status: 'processing',
            error_message: null
          })
          .select()
          .single();

        if (resumeError) throw resumeError;
        if (!resumeData) throw new Error('Failed to create resume record');

        resumeId = resumeData.id;

        const payload = {
          resumeId: resumeData.id,
          userId: user.id,
          cvUrl: uploadedFileUrl,
          jobDesc: formData.isUrlMode ? null : formData.jobDescription,
          jobUrl: formData.isUrlMode ? formData.jobUrl : null,
          enhanceCV: formData.enhanceCV,
          authToken: session.access_token
        };

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 120000);

        try {
          const response = await fetch('https://elquesgoozjgtmkiqrbd.supabase.co/functions/v1/process-cv', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              'Authorization': `Bearer ${session.access_token}`
            },
            body: JSON.stringify(payload),
            signal: controller.signal
          });

          clearTimeout(timeoutId);

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({ error: 'Failed to process resume' }));
            throw new Error(errorData.error || 'Failed to process resume');
          }

          const responseData = await response.json();
          
          if (responseData.status === 'error') {
            throw new Error(responseData.error || 'Failed to process resume');
          }

          await Promise.all([fetchResumes(), fetchUserPlan()]);

        } catch (fetchError) {
          if (fetchError.name === 'AbortError') {
            console.log('Request timeout - processing continues in background');
          } else {
            throw fetchError;
          }
        }

      } catch (error) {
        throw error;
      }

    } catch (error) {
      console.error('Error processing resume:', error);
      
      const errorMessage = error instanceof Error 
        ? error.message
        : 'An unexpected error occurred. Please try again.';
      
      alert(errorMessage);

      if (uploadedFileUrl && !resumeId) {
        try {
          await supabase.storage
            .from('resumes')
            .remove([uploadedFileUrl.split('/').pop()!]);
        } catch (cleanupError) {
          console.error('Failed to cleanup uploaded file:', cleanupError);
        }
      }

    } finally {
      setIsProcessing(false);
      setShowProcessingMessage(false);
      await Promise.all([fetchResumes(), fetchUserPlan()]);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {!isLoading && showProModal && <ProModal onClose={() => setShowProModal(false)} />}
      {showReferralModal && <ReferralModal onClose={() => setShowReferralModal(false)} referralCode={referralCode} />}
      
      <header className="bg-white shadow-sm">
        <DashboardHeader
          onOpenReferralModal={() => setShowReferralModal(true)}
          onOpenProModal={() => setShowProModal(true)}
          onSignOut={signOut}
          userPlan={userPlan}
          isLoading={isLoading}
          isMobile={false}
        />

        {isMobileMenuOpen && (
          <div className="md:hidden mt-4 space-y-4 border-t pt-4 px-4">
            <div className="text-sm font-medium">
              <span className="text-gray-500">Remaining resumes: </span>
              <span className={`font-bold ${remainingResumes > 0 ? 'text-green-600' : 'text-red-600'}`}>
                {remainingResumes}
              </span>
            </div>
            <UserMenu onSignOut={signOut} />
          </div>
        )}
      </header>

      <main className="flex-1 overflow-y-auto p-4">
        <div className="max-w-3xl mx-auto">
          {connectionError && (
            <div className="mb-6 bg-red-50 border-l-4 border-red-400 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <AlertTriangle className="h-5 w-5 text-red-400" />
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-700">
                    Unable to connect to the server. Please check your internet connection and try again.
                  </p>
                </div>
              </div>
            </div>
          )}

          {!isLoading && remainingResumes <= 0 && userPlan?.plan_type === 'free' && (
            <div className="mb-6 bg-blue-50 border-l-4 border-blue-400 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <AlertTriangle className="h-5 w-5 text-blue-400" />
                </div>
                <div className="ml-3">
                  <p className="text-sm text-blue-700">
                    You've used all your free conversions. Upgrade to Pro to get 50 more conversions for just $5!{' '}
                    <button
                      onClick={() => setShowProModal(true)}
                      className="font-medium underline hover:text-blue-800"
                    >
                      Learn more
                    </button>
                  </p>
                </div>
              </div>
            </div>
          )}

          {showProcessingMessage && (
            <div className="mb-6 bg-blue-50 border-l-4 border-blue-400 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <Clock className="h-5 w-5 text-blue-400" />
                </div>
                <div className="ml-3">
                  <p className="text-sm text-blue-700">
                    Your resume is being processed. This typically takes about a minute. You may browse to other tabs, but please <span className="font-bold">keep this window open</span> until the process is complete.
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
              Создай идеальное резюме
            </h1>
            <p className="text-base md:text-lg text-gray-600">
              Адаптируй резюме под каждую вакансию и повысь шансы получить оффер
            </p>
          </div>

          <ResumeUploadForm
            onSubmit={handleFormSubmit}
            isProcessing={isProcessing}
            remainingResumes={remainingResumes}
          />

          <div className="mt-8">
            <h2 className="text-2xl font-semibold mb-4">Your Resumes</h2>
            <ResumeList
              resumes={resumes}
              onDownload={handleDownload}
            />
          </div>
        </div>
      </main>
    </div>
  );
}